// components/SearchBar.js
import React, { useState, useRef, useEffect } from 'react';

const SearchBar = ({ value, onChange, suggestions, onSelectSuggestion }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const suggestionItemsRef = useRef([]);

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setHighlightedIndex(-1);
    suggestionItemsRef.current = [];
  }, [suggestions]);

  // Force show suggestions when new suggestions arrive and input is focused
  useEffect(() => {
    if (suggestions && suggestions.length > 0 && document.activeElement === inputRef.current) {
      setShowSuggestions(true);
    } else if (suggestions && suggestions.length === 0) {
      setShowSuggestions(false);
    }
  }, [suggestions]);

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || !suggestions || suggestions.length === 0) return;

    // Down Arrow
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prevIndex => {
        const newIndex = prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0;
        scrollToHighlighted(newIndex);
        return newIndex;
      });
    }
    // Up Arrow
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prevIndex => {
        const newIndex = prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1;
        scrollToHighlighted(newIndex);
        return newIndex;
      });
    }
    // Enter - select the highlighted suggestion
    else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[highlightedIndex]);
    }
    // Escape - close suggestions
    else if (e.key === 'Escape') {
      e.preventDefault();
      setShowSuggestions(false);
    }
  };

  // Scroll to highlighted suggestion
  const scrollToHighlighted = (index) => {
    if (suggestionItemsRef.current[index]) {
      suggestionItemsRef.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  };

  const handleInputChange = (newValue) => {
    onChange(newValue);
  };

  const handleSuggestionClick = (suggestion) => {
    // Important: Clear suggestions from parent component immediately
    // This prevents the prefix grouping from showing up
    onSelectSuggestion(suggestion);
    setShowSuggestions(false);
    setHighlightedIndex(-1);

    // Focus back on input after selection
    inputRef.current.focus();
  };

  // Handle microphone button click for voice search
  const handleMicrophoneClick = () => {
    // Check if the browser supports the Web Speech API
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.lang = 'en-US';
      recognition.interimResults = false;

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        handleInputChange(transcript);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        setIsListening(false);
      };

      recognition.start();
    } else {
      alert('Your browser does not support speech recognition. Please try using a different browser.');
    }
  };

  return (
    <div className="search-container">
      <div className="search-input-container">
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder="Search for movies, actors, directors..."
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Show suggestions on focus if they exist
            if (suggestions && suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
        />
        <button
          className="microphone-button"
          onClick={handleMicrophoneClick}
          title="Search by voice"
        >
          {isListening ? (
            // Animated microphone when listening
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="microphone-icon pulsing">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          ) : (
            // Static microphone when not listening
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="microphone-icon">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          )}
        </button>
      </div>

      {(showSuggestions && suggestions && suggestions.length > 0) && (
        <div
          ref={suggestionsRef}
          className="suggestions-container"
        >
          <p className="suggestions-header">
            Suggestions ({suggestions.length})
          </p>
          {suggestions.slice(0, 10).map((suggestion, index) => (
            <div
              ref={el => suggestionItemsRef.current[index] = el}
              key={`${suggestion}-${index}`}
              className={`suggestion-item ${index === highlightedIndex ? 'highlighted' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion}
            </div>
          ))}
          <button
            className="close-suggestions"
            onClick={() => {
              setShowSuggestions(false);
            }}
          >
            Close Suggestions
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
